/*
 * HOW A CLIP IS KEPT. A visit's record is a dozen short words (country, browser, device, traffic source, ...) that repeat across thousands of visits, so written out in full a busy day came to 11 MB (about 630 bytes a
 * visit), which no browser will keep. Here each distinct word is written ONCE (a dictionary) and a visit refers to it by number: about a fifth of the size, and what a server would store (and gzip) too. Nothing is lost:
 * `expandActions(packActions(a))` gives back exactly what the replay plays. Pure, so it is tested without a browser.
 */
const FIELDS = ["country_name", "country_code", "os", "browser", "device", "traffic_source", "search_engine", "social_network", "visitor_type", "visitor_category", "current_url_name", "goalUrl", "referrer"] as const;
const ABSENT = -1;

export interface Packed {
  dict: string[];
  /** arrival:   [0, t, n, goalReached (1 true, 0 false, -1 not there), ...one dictionary number per field (-1 = none)]
   *  pageview:  [1, t, n, dictionary number of the page, goalReached(0/1)]
   *  departure: [2, t, n]
   *  `n` is the visit's number: the visit is called `s<n>` */
  rows: number[][];
}

const visitNumber = (id: unknown): number => Number(String(id).replace(/^s/, ""));

export const packActions = (actions: any[]): Packed => {
  const dict: string[] = [];
  const index = new Map<string, number>();
  const word = (value: unknown): number => {
    if (typeof value !== "string") return ABSENT;
    let at = index.get(value);
    if (at === undefined) { at = dict.length; dict.push(value); index.set(value, at); }

    return at;
  };
  const rows: number[][] = [];
  for (const a of actions || []) {
    if (a.type === "arrival") rows.push([0, a.t, visitNumber(a.session?._id), a.session?.goalReached === true ? 1 : a.session?.goalReached === false ? 0 : ABSENT, ...FIELDS.map((f) => word(a.session?.[f]))]);
    else if (a.type === "pageview") rows.push([1, a.t, visitNumber(a.sessionId), word(a.pathName), a.goalReached ? 1 : 0]);
    else if (a.type === "departure") rows.push([2, a.t, visitNumber(a.sessionId)]);
  }

  return { dict, rows };
};

export const expandActions = (packed: Packed | undefined): any[] => {
  const dict = packed?.dict || [];
  const out: any[] = [];
  for (const r of packed?.rows || []) {
    const id = `s${r[2]}`;
    if (r[0] === 0) {
      const session: any = { _id: id, visitor_id: id, createdAt: r[1], session_end_time: null, events: [] };
      FIELDS.forEach((f, i) => { const at = r[4 + i]; if (at !== ABSENT && at !== undefined) session[f] = dict[at]; });
      if (!session.referrer) session.referrer = "/";
      if (r[3] === 1) session.goalReached = true;
      else if (r[3] === 0) session.goalReached = false;
      out.push({ type: "arrival", t: r[1], session });
    } else if (r[0] === 1) {
      out.push({ type: "pageview", t: r[1], sessionId: id, pathName: dict[r[3]], ...(r[4] ? { goalReached: true } : {}) });
    } else if (r[0] === 2) {
      out.push({ type: "departure", t: r[1], sessionId: id });
    }
  }

  return out;
};
